import { forwardRef, Suspense, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { ToolManagerRef } from '../ToolManager'
import { useProject } from '../hooks'
import { Shape, Vector2, Vector3, Vector3Like } from 'three'
import { Circle, Line, Sphere } from '@react-three/drei'
import { useAppDispatch, useAppSelector, useAppStore } from '~/store/hooks'
import { addObstruction } from '~/store/objects'
import LayerKeys from '~/config/layerKeys'
import { guid } from '~/lib/utils'
import { modelToUI, objectUIToModel, uiToModel } from '../util/units'
import Util from '../lib/util'
import { DetailedObstruction } from '~/config/detailedObstructions'
import { checkInFacility } from './util'
import { clearStatus, setStatus } from '~/store/status/actions'
import { productDistanceEngine } from '../lib/productDistanceEngine'
import { circlePoints } from '../util/circlePoints'
import { DetailedObstructionMesh } from '~/components/DrawingCanvas/Obstructions/components/DetailedObstructionMesh'
import { snapToCardinals, snapToPointOrtho } from '~/components/DrawingCanvas/util/snaplines'
import { CURSOR_Z } from '~/components/DrawingCanvas/constants/zIndices'

const MINIMUM_RECTANGLE_SIZE = 15
const MINIMUM_CIRCLE_SIZE = 250

function useMakeObstruction(height = 48) {
  const dispatch = useAppDispatch()

  return useCallback(
    (points: Vector3Like[], obstructionType = 'basic', resizable = true) => {
      const pts = points.map(point => ({ ...objectUIToModel(point), z: 0 }))
      const centroid = Util.polygonCentroid(pts)

      if (!checkInFacility(pts, centroid, 0)) {
        dispatch(setStatus({ type: 'error', text: 'Obstructions must be placed in the facility!' }))
        return false
      }

      dispatch(clearStatus({ type: 'error' }))
      dispatch(
        addObstruction({
          obstruction: {
            id: guid(),
            height,
            layerKey: LayerKeys.OBSTRUCTIONS,
            obstructionType,
            position: { x: centroid.x, y: centroid.y, z: centroid.z },
            positions: pts,
            resizable,
            rotation: { x: 0, y: 0, z: 0 },
          },
        })
      )
      return true
    },
    [height]
  )
}

export const ObstructionRectTool = forwardRef<ToolManagerRef>(function ObstructionRectTool(_props, ref) {
  const project = useProject()
  const [[x, y], setPoint] = useState([0, 0])
  const [pointOne, setPointOne] = useState<[x: number, y: number] | undefined>(undefined)
  const makeObstruction = useMakeObstruction()

  useImperativeHandle(
    ref,
    () => ({
      onMouseMove(event) {
        const point = project(event)
        if (point) {
          setPoint([point.x, point.y])
        }
        return true
      },
      onMouseDown() {
        if (pointOne === undefined) {
          setPointOne([x, y])
        }
        return true
      },
      onMouseUp() {
        if (pointOne === undefined) {
          return true
        }

        const size = new Vector2(x, y).distanceToSquared({ x: pointOne[0], y: pointOne[1] })
        if (size < MINIMUM_RECTANGLE_SIZE) {
          setPointOne(undefined)
          return true
        }

        const minX = Math.min(x, pointOne[0])
        const maxX = Math.max(x, pointOne[0])
        const minY = Math.min(y, pointOne[1])
        const maxY = Math.max(y, pointOne[1])

        const points = [
          new Vector3(minX, minY, CURSOR_Z),
          new Vector3(minX, maxY, CURSOR_Z),
          new Vector3(maxX, maxY, CURSOR_Z),
          new Vector3(maxX, minY, CURSOR_Z),
        ]
        if (!makeObstruction(points)) {
          setPointOne(undefined)
          return true
        }

        return true
      },
    }),
    [project, x, y, pointOne]
  )

  if (pointOne === undefined || (pointOne[0] === x && pointOne[1] === y)) {
    return <Circle position={new Vector3(x, y, CURSOR_Z)} args={[2, 20]} material-color="pink" />
  }

  const minX = Math.min(x, pointOne[0])
  const maxX = Math.max(x, pointOne[0])
  const minY = Math.min(y, pointOne[1])
  const maxY = Math.max(y, pointOne[1])

  const height = 48

  const points = [
    new Vector3(minX, minY, CURSOR_Z),
    new Vector3(minX, maxY, CURSOR_Z),
    new Vector3(maxX, maxY, CURSOR_Z),
    new Vector3(maxX, minY, CURSOR_Z),
  ]

  return (
    <>
      <Circle position={new Vector3(x, y, CURSOR_Z)} args={[2, 20]} material-color="pink" />

      <mesh>
        <extrudeGeometry
          args={[new Shape(points.map(pt => new Vector2().copy(pt))), { depth: height }]}
          attach="geometry"
        />
        <meshStandardMaterial color="gray" transparent opacity={0.4} />
      </mesh>

      <group position={new Vector3(0, 0, height)}>
        <Line points={[...points, points[0]]} color="gray" dashed lineWidth={2} />
      </group>
      <Line points={[...points, points[0]]} color="gray" dashed lineWidth={2} />
    </>
  )
})

export const ObstructionCircleTool = forwardRef<ToolManagerRef>(function ObstructionRectTool(_props, ref) {
  const project = useProject()
  const [[x, y], setPoint] = useState([0, 0])
  const [pointOne, setPointOne] = useState<[x: number, y: number] | undefined>(undefined)
  const makeObstruction = useMakeObstruction()

  useImperativeHandle(
    ref,
    () => ({
      onMouseMove(event) {
        const point = project(event)
        if (point) {
          setPoint([point.x, point.y])
        }
        return true
      },
      onMouseDown() {
        if (pointOne === undefined) {
          setPointOne([x, y])
        }
        return true
      },
      onMouseUp() {
        if (pointOne === undefined) {
          return true
        }

        const size = new Vector2(x, y).distanceToSquared({ x: pointOne[0], y: pointOne[1] })
        if (size < MINIMUM_CIRCLE_SIZE) {
          setPointOne(undefined)
          return true
        }

        const points = circlePoints(x, y, pointOne[0], pointOne[1])
        if (!makeObstruction(points)) {
          setPointOne(undefined)
          return true
        }

        return true
      },
    }),
    [project, x, y, pointOne]
  )

  if (pointOne === undefined || (pointOne[0] === x && pointOne[1] === y)) {
    return <Circle position={new Vector3(x, y, CURSOR_Z)} args={[2, 20]} material-color="pink" />
  }

  const height = 48
  const points = circlePoints(x, y, pointOne[0], pointOne[1])

  return (
    <>
      <Circle position={new Vector3(x, y, CURSOR_Z)} args={[2, 20]} material-color="pink" />

      <mesh>
        <extrudeGeometry
          args={[new Shape(points.map(pt => new Vector2().copy(pt))), { depth: height }]}
          attach="geometry"
        />
        <meshStandardMaterial color="gray" transparent opacity={0.4} />
      </mesh>

      <group position={new Vector3(0, 0, height)}>
        <Line points={[...points, points[0]]} color="gray" dashed lineWidth={2} />
      </group>
      <Line points={[...points, points[0]]} color="gray" dashed lineWidth={2} />
    </>
  )
})

function useMakeCustomObstructionOnUnmount(points: Vector3[]) {
  const makeObstruction = useMakeObstruction()
  const store = useAppStore()

  useEffect(
    () => () => {
      if (points.length < 3) {
        return
      }
      if (store.getState().tools.activeTool === 'OBSTRUCTION_POLYGON_TOOL') {
        return
      }

      makeObstruction(points, 'basic', false)
    },
    [points, store]
  )
}

export const ObstructionCustomTool = forwardRef<ToolManagerRef>(function ObstructionCustomTool(_props, ref) {
  const project = useProject()
  const isOrthoModeEnabled = useAppSelector(state => state.tools.isOrthoModeEnabled)
  const [[x, y], setPoint] = useState([0, 0])
  const [points, setPoints] = useState<Vector3[]>([])
  const dispatch = useAppDispatch()

  useMakeCustomObstructionOnUnmount(points)

  useImperativeHandle(
    ref,
    () => ({
      onMouseMove(event) {
        const point = project(event)
        if (point) {
          const vec3Point = new Vector3(point.x, point.y, CURSOR_Z)
          const snapPoints = points.map(p => ({ id: '', point: p }))
          snapPoints.pop()
          if (event.shiftKey || (isOrthoModeEnabled && points.length > 0)) {
            const lastPoint = points[points.length - 1]
            const snappedToCardinals = snapToCardinals(lastPoint, vec3Point)
            const snappedOrthoPoint = snapToPointOrtho(snapPoints, snappedToCardinals)
            const newPoint = snappedOrthoPoint?.snapPoint ?? snappedToCardinals
            setPoint([newPoint.x, newPoint.y])
          } else {
            const newPoint = snapToPointOrtho(snapPoints, vec3Point)?.snapPoint ?? vec3Point
            setPoint([newPoint.x, newPoint.y])
          }
        }
        return true
      },
      onMouseDown() {
        return true
      },
      onMouseUp() {
        const modelPoint = new Vector3(uiToModel(x), uiToModel(y), 0)
        if (!productDistanceEngine.isWithinFacility(modelPoint)) {
          dispatch(setStatus({ type: 'error', text: 'Obstructions must be placed in the facility!' }))
        } else {
          setPoints(pts => [...pts, new Vector3(x, y, CURSOR_Z)])
          dispatch(clearStatus({ type: 'error' }))
        }

        return true
      },
    }),
    [project, x, y, isOrthoModeEnabled, points]
  )

  if (points.length === 0) {
    return <Circle position={new Vector3(x, y, CURSOR_Z)} args={[2, 20]} material-color="pink" />
  }

  if (points.length < 3) {
    return (
      <>
        <Circle position={new Vector3(x, y, CURSOR_Z)} args={[2, 20]} material-color="pink" />
        <Line points={[...points, new Vector3(x, y, CURSOR_Z)]} color="gray" dashed lineWidth={2} />
      </>
    )
  }

  const height = 48
  const committedPoints = points
  const pendingPoints = [...points, new Vector3(x, y, CURSOR_Z)]

  return (
    <>
      <Circle position={new Vector3(x, y, CURSOR_Z)} args={[2, 20]} material-color="pink" />

      <mesh>
        <extrudeGeometry
          args={[new Shape(committedPoints.map(pt => new Vector2().copy(pt))), { depth: height }]}
          attach="geometry"
        />
        <meshStandardMaterial color="gray" transparent opacity={0.4} />
      </mesh>

      <group position={new Vector3(0, 0, height)}>
        <Line points={[...committedPoints, committedPoints[0]]} color="gray" dashed lineWidth={2} />
      </group>
      <Line points={[...committedPoints, committedPoints[0]]} color="gray" dashed lineWidth={2} />

      <group position={new Vector3(0, 0, height)}>
        <Line points={[...pendingPoints, pendingPoints[0]]} color="gray" dashed dashSize={2} lineWidth={2} />
      </group>
      <Line points={[...pendingPoints, pendingPoints[0]]} color="gray" dashed dashSize={2} lineWidth={2} />
    </>
  )
})

export const ObstructionModelTool = forwardRef<ToolManagerRef, { activeToolProps: DetailedObstruction }>(
  function ObstructionModelTool({ activeToolProps }, ref) {
    const project = useProject()
    const [[x, y], setPoint] = useState([0, 0])
    const makeObstruction = useMakeObstruction(activeToolProps.height)

    useImperativeHandle(
      ref,
      () => ({
        onMouseMove(event) {
          const point = project(event)
          if (point) {
            setPoint([point.x, point.y])
          }
          return true
        },
        onMouseDown(_event) {
          return true
        },
        onMouseUp(_event) {
          const minX = uiToModel(x) - (activeToolProps.width * 12) / 2
          const maxX = uiToModel(x) + (activeToolProps.width * 12) / 2

          const minY = uiToModel(y) - (activeToolProps.length * 12) / 2
          const maxY = uiToModel(y) + (activeToolProps.length * 12) / 2

          const boundingBoxPoints = [
            new Vector3(modelToUI(minX), modelToUI(minY), 0),
            new Vector3(modelToUI(minX), modelToUI(maxY), 0),
            new Vector3(modelToUI(maxX), modelToUI(maxY), 0),
            new Vector3(modelToUI(maxX), modelToUI(minY), 0),
          ]

          makeObstruction(boundingBoxPoints, activeToolProps.obstructionType, activeToolProps.resizable)
          return true
        },
      }),
      [project, x, y, activeToolProps]
    )

    const minX = uiToModel(x) - (activeToolProps.width * 12) / 2
    const maxX = uiToModel(x) + (activeToolProps.width * 12) / 2

    const minY = uiToModel(y) - (activeToolProps.length * 12) / 2
    const maxY = uiToModel(y) + (activeToolProps.length * 12) / 2

    const boundingBoxPoints = [
      new Vector3(minX, minY, activeToolProps.height),
      new Vector3(minX, maxY, activeToolProps.height),
      new Vector3(maxX, maxY, activeToolProps.height),
      new Vector3(maxX, minY, activeToolProps.height),
      new Vector3(minX, minY, activeToolProps.height),
    ]

    return (
      <group position={new Vector3(x, y, 0)}>
        <Suspense fallback={<Sphere />}>
          <DetailedObstructionMesh
            obstruction={{
              obstructionType: activeToolProps.obstructionType,
              height: activeToolProps.height,
              positions: boundingBoxPoints,
            }}
          />
        </Suspense>
      </group>
    )
  }
)
